/**
 * Persisted "view" state for an Analysis table — which columns are hidden and
 * what the rows are grouped by. Same discipline as the filters in
 * `table-filters.ts`: scoped per user × bid × table, every storage access
 * guarded, and a payload that fails validation is discarded rather than
 * half-applied.
 *
 * A view is a preference, never a correctness input: a hidden column removes a
 * column, not a row, so degrading to the default view is always safe.
 */

import { filterStorageKey } from '@/lib/table-filters'

export interface TableViewState {
  /** Column ids the user has hidden. Order is irrelevant. */
  hiddenColumns: string[]
  /** Column id the rows are grouped by, or null for "None". */
  groupBy: string | null
}

/**
 * Shares the filter key's user × bid × table scope, plus a `:view` suffix.
 *
 * Deriving it rather than building a second prefix keeps the two in step — the
 * user-scoping fix in `filterStorageKey` applies here for free, and the legacy
 * purge (which tells keys apart by segment count) never sees a view key,
 * because `bidfilters:<user>:<bid>:<table>:view` is five segments.
 */
export function tableViewStorageKey(
  userId: string | null | undefined,
  bidId: string,
  tableKey: string
): string {
  return `${filterStorageKey(userId, bidId, tableKey)}:view`
}

export function readTableView(key: string): TableViewState | null {
  let parsed: unknown
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null

  const { hiddenColumns, groupBy } = parsed as Record<string, unknown>
  if (!Array.isArray(hiddenColumns) || hiddenColumns.some((c) => typeof c !== 'string')) return null
  if (groupBy !== undefined && groupBy !== null && typeof groupBy !== 'string') return null

  return {
    hiddenColumns: hiddenColumns as string[],
    groupBy: typeof groupBy === 'string' ? groupBy : null,
  }
}

export function writeTableView(key: string, view: TableViewState): void {
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ hiddenColumns: view.hiddenColumns, groupBy: view.groupBy })
    )
  } catch {
    /* private mode / quota — degrade to in-memory state, never throw */
  }
}
